import {useReports} from '@sail/observability';
import {supplemental} from '@stripe-internal/cldr';
import {useMemo} from 'react';
import createGlobalizeWithLocale from 'src/internal/createGlobalizeWithLocale/createGlobalizeWithLocale';
import useIntlConfig from 'src/public/useIntlConfig';

import type {CalendarData} from 'src/types';

export default function useCalendarData(): CalendarData {
  const reports = useReports();
  const config = useIntlConfig();

  const globalize = createGlobalizeWithLocale(
    config,
    config.locale,
    undefined,
    reports,
  );

  return useMemo(() => {
    const months: Record<string, Record<string, string>> | undefined =
      globalize.cldr.main('dates/calendars/gregorian/months/format');
    const days: Record<string, Record<string, string>> | undefined =
      globalize.cldr.main('dates/calendars/gregorian/days/format');
    const dateFormats: Record<string, string> | undefined = globalize.cldr.main(
      'dates/calendars/gregorian/dateFormats',
    );
    const dateFields: Record<string, Record<string, string>> | undefined =
      globalize.cldr.main('dates/fields');

    const weekFirstDay =
      supplemental.cldrWeekData.supplemental.weekData.firstDay;
    const weekMinDays = supplemental.cldrWeekData.supplemental.weekData.minDays;

    return {
      gregorian: {
        months: {
          format: {
            abbreviated: months?.abbreviated,
            short: months?.short,
            narrow: months?.narrow,
            wide: months?.wide,
          },
        },
        days: {
          format: {
            abbreviated: days?.abbreviated,
            short: days?.short,
            narrow: days?.narrow,
            wide: days?.wide,
          },
        },
        dateFormats: {
          short: dateFormats?.short,
        },
      },
      weekData: {
        firstDay: weekFirstDay,
        minDays: weekMinDays,
      },
      dateFields: {
        week: {
          displayName: dateFields?.week?.displayName,
          short: dateFields?.['week-short']?.displayName,
          narrow: dateFields?.['week-narrow']?.displayName,
        },
      },
    };
  }, [globalize]);
}
